/**
 * Client-side JWT expiry check — a faithful port of
 * `AuthService.isTokenExpired` (auth.service.ts:232-247).
 *
 * Two behaviours preserved deliberately, because route access depends on them:
 *   - A token with NO `exp` claim is treated as VALID (line 242 returns false).
 *   - A token that fails to parse is treated as EXPIRED (line 245 returns true).
 *
 * There is no refresh-token flow anywhere in the backend contract, so an expired
 * token means a hard logout. Nothing here contacts the server.
 */

interface JwtPayload {
  exp?: number;
  [key: string]: unknown;
}

/** Base64URL-decode a JWT segment. `atob` alone mishandles `-`/`_` and padding. */
function decodeSegment(segment: string): string {
  const base64 = segment.replace(/-/g, '+').replace(/_/g, '/');
  const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
  const binary = atob(padded);
  // Handle multi-byte UTF-8 in claims (e.g. a non-ASCII username).
  const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
  return new TextDecoder().decode(bytes);
}

function decodeJwtPayload(token: string): JwtPayload | null {
  try {
    if (!token.includes('.')) return null;
    const segment = token.split('.')[1];
    if (!segment) return null;
    return JSON.parse(decodeSegment(segment)) as JwtPayload;
  } catch {
    return null;
  }
}

export function isTokenExpired(token: string | null | undefined): boolean {
  if (!token) return true;

  // A token without a `.` was never a JWT; Angular fell through to `return false`
  // (valid) for that case rather than treating it as expired.
  if (!token.includes('.')) return false;

  const payload = decodeJwtPayload(token);
  if (payload === null) return true; // parse failure ⇒ expired (Angular line 245)
  if (typeof payload.exp !== 'number') return false; // no exp ⇒ valid (Angular line 242)

  return payload.exp * 1000 < Date.now();
}

/**
 * The signed-in user's email address, read out of the token itself.
 *
 * ⚠️ THIS EXISTS FOR SSO, AND IT IS NOT A NICETY. Password login learns who the user is from the
 * `/users/login` response body; the SSO redirect hands over a bare token and nothing else. The email is
 * this app's ONLY identity anchor — `useDelegationContext` resolves the signed-in user's id by matching
 * it against the directory, because neither the session nor `/management/userinfo` carries an id — so an
 * SSO session with a blank email silently loses "My Delegations".
 *
 * SEVERAL CLAIM NAMES ARE TRIED because two different issuers are in play and only one of them is
 * documented anywhere: the backend mints its own `access` token (`sub`), while the values Azure AD itself
 * would put in are `preferred_username` / `upn` / `unique_name`. Trying all of them costs one loop and
 * removes the need to guess which one this deployment ends up using.
 *
 * ⚠️ THE VALUE MUST LOOK LIKE AN EMAIL. `sub` is very often an opaque user id — a UUID or an integer —
 * and storing that as `user.email` would be worse than storing nothing: `greetingName` would render
 * "Welcome back, 3f2a1b" and the delegation lookup would match no one while appearing to have an
 * identity. Returning `null` keeps the failure honest and visible.
 */
const EMAIL_CLAIMS = ['email', 'user_email', 'preferred_username', 'upn', 'unique_name', 'sub'] as const;

export function emailFromToken(token: string | null | undefined): string | null {
  if (!token) return null;

  const payload = decodeJwtPayload(token);
  if (!payload) return null;

  for (const claim of EMAIL_CLAIMS) {
    const value = payload[claim];
    if (typeof value !== 'string') continue;

    const trimmed = value.trim();
    // Deliberately loose: one `@`, something either side, no whitespace. A stricter pattern would
    // start rejecting real addresses, and this is a display/lookup value, not a validation gate.
    if (/^[^\s@]+@[^\s@]+$/.test(trimmed)) return trimmed;
  }

  return null;
}
